import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config';
import { getCurrentPeriod, getPeriodForDate } from '@/lib/utils/periods';
import { responseDocId } from '@/lib/assessment/questionnaireResponseKey';

const COLLECTION_NAME = 'questionersanswers';

export interface QuestionerAnswer {
  questionId: string;
  optionId: string;
  score: number;
  questionText?: {
    en: string;
    ar: string;
  };
  optionText?: {
    en: string;
    ar: string;
  };
  category?: string;
}

export interface QuestionerResponse {
  id: string;
  userId: string;
  questionnaireId: string;
  questionnaireName: {
    en: string;
    ar: string;
  };
  score: number;
  maxScore: number;
  completedAt: Date;
  periodId: string; // e.g., "2024-Q1"
  answers: QuestionerAnswer[];
  createdBy?: string; // Who administered the questionnaire (examiner, coach, etc.)
}

// Save questionnaire answers
export async function saveQuestionerAnswers(
  userId: string, 
  questionnaireId: string,
  questionnaireName: { en: string; ar: string },
  score: number, 
  answers: QuestionerAnswer[], 
  maxScore: number, 
  periodId?: string,
  createdBy?: string
): Promise<string> {
  try {
    const currentPeriod = getCurrentPeriod();
    const period = periodId || currentPeriod.id;
    const completedAt = new Date();
    // One document per Riyadh month (#124): a monthly refill keeps last month's
    // response. Older documents are keyed by quarter; readers take the newest
    // by completedAt, so both shapes coexist.
    const responseId = responseDocId(userId, questionnaireId, completedAt);
    const responseRef = doc(db, COLLECTION_NAME, responseId);
    
    console.log('💾 [saveQuestionerAnswers] Saving questionnaire response:', {
      userId,
      questionnaireId,
      periodId: period,
      responseId,
      score,
      maxScore,
      answersCount: answers.length
    });
    
    const responseData: Omit<QuestionerResponse, 'id'> = {
      userId,
      questionnaireId,
      questionnaireName,
      score,
      maxScore,
      completedAt,
      periodId: period,
      answers,
      createdBy
    };
    
    await setDoc(responseRef, {
      ...responseData,
      completedAt: Timestamp.fromDate(responseData.completedAt)
    });
    
    console.log('✅ [saveQuestionerAnswers] Successfully saved questionnaire response:', responseId);
    
    return responseId;
  } catch (error) {
    console.error('Error saving questioner answers:', error);
    throw error;
  }
}

// Get questionnaire response for a user in a specific period
export async function getQuestionerResponse(
  userId: string, 
  questionnaireId: string,
  periodId?: string
): Promise<QuestionerResponse | null> {
  try {
    const currentPeriod = getCurrentPeriod();
    const period = periodId || currentPeriod.id;
    const responseId = `${userId}_${questionnaireId}_${period}`;
    const responseRef = doc(db, COLLECTION_NAME, responseId);
    const responseDoc = await getDoc(responseRef);
    
    if (responseDoc.exists()) {
      const data = responseDoc.data();
      return {
        id: responseDoc.id,
        ...data,
        completedAt: data.completedAt?.toDate() || new Date()
      } as QuestionerResponse;
    }
    return null;
  } catch (error) {
    console.error('Error getting questioner response:', error);
    throw error;
  }
}

// Get all questionnaire responses for a user
export async function getUserQuestionerHistory(userId: string): Promise<QuestionerResponse[]> {
  try {
    const responsesQuery = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('completedAt', 'desc')
    );
    const snapshot = await getDocs(responsesQuery);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate() || new Date()
      } as QuestionerResponse;
    });
  } catch (error) {
    console.error('Error getting user questioner history:', error);
    return [];
  }
}

// Get all responses for a specific questionnaire
export async function getQuestionnaireResponses(questionnaireId: string): Promise<QuestionerResponse[]> {
  try {
    const responsesQuery = query(
      collection(db, COLLECTION_NAME),
      where('questionnaireId', '==', questionnaireId),
      orderBy('completedAt', 'desc')
    );
    const snapshot = await getDocs(responsesQuery);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate() || new Date()
      } as QuestionerResponse;
    });
  } catch (error) {
    console.error('Error getting questionnaire responses:', error);
    return [];
  }
}

// Check if user has completed a questionnaire in a specific period
export async function hasCompletedQuestioner(
  userId: string, 
  questionnaireId: string,
  periodId?: string
): Promise<boolean> {
  try {
    const response = await getQuestionerResponse(userId, questionnaireId, periodId);
    return response !== null;
  } catch (error) {
    console.error('Error checking questioner completion:', error);
    return false;
  }
}

// Check if user can create a new questionnaire response
export async function canCreateNewQuestionerResponse(
  userId: string, 
  questionnaireId: string
): Promise<boolean> {
  try {
    const currentPeriod = getCurrentPeriod();
    return !(await hasCompletedQuestioner(userId, questionnaireId, currentPeriod.id));
  } catch (error) {
    console.error('Error checking if can create new questioner response:', error);
    return false;
  }
}

// Get user's latest questionnaire response (for backward compatibility)
export async function getUserLatestScore(userId: string, periodId?: string): Promise<QuestionerResponse | null> {
  try {
    const currentPeriod = getCurrentPeriod();
    const period = periodId || currentPeriod.id;
    
    console.log('🔍 [getUserLatestScore] Searching for questionnaire response:', {
      userId,
      periodId: period,
      collection: COLLECTION_NAME
    });
    
    const responsesQuery = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('periodId', '==', period),
      orderBy('completedAt', 'desc')
    );
    const snapshot = await getDocs(responsesQuery);
    
    console.log('📋 [getUserLatestScore] Query results:', {
      userId,
      periodId: period,
      found: !snapshot.empty,
      count: snapshot.docs.length
    });
    
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      const response = {
        id: snapshot.docs[0].id,
        ...data,
        completedAt: data.completedAt?.toDate() || new Date()
      } as QuestionerResponse;
      
      console.log('✅ [getUserLatestScore] Found questionnaire response:', {
        responseId: response.id,
        score: response.score,
        maxScore: response.maxScore,
        completedAt: response.completedAt
      });
      
      return response;
    }
    
    console.log('❌ [getUserLatestScore] No questionnaire response found for user:', userId, 'period:', period);
    return null;
  } catch (error) {
    console.error('Error getting user latest score:', error);
    return null;
  }
}

// Legacy compatibility functions (to maintain backward compatibility with existing code)
export interface UserScore {
  userId: string;
  score: number;
  maxScore: number;
  completedAt: Date;
  periodId: string;
  answers: {
    questionId: string;
    optionId: string;
    score: number;
  }[];
}

// Convert QuestionerResponse to legacy UserScore format
function convertToLegacyUserScore(response: QuestionerResponse): UserScore {
  return {
    userId: response.userId,
    score: response.score,
    maxScore: response.maxScore,
    completedAt: response.completedAt,
    periodId: response.periodId,
    answers: response.answers.map(answer => ({
      questionId: answer.questionId,
      optionId: answer.optionId,
      score: answer.score
    }))
  };
}

// Legacy function for backward compatibility
export async function saveUserScore(
  userId: string, 
  score: number, 
  answers: UserScore['answers'], 
  maxScore: number, 
  periodId?: string
): Promise<void> {
  try {
    // Convert legacy answers to new format
    const questionerAnswers: QuestionerAnswer[] = answers.map(answer => ({
      questionId: answer.questionId,
      optionId: answer.optionId,
      score: answer.score
    }));
    
    // Use default questionnaire for legacy saves
    await saveQuestionerAnswers(
      userId,
      'default', // Default questionnaire ID for legacy compatibility
      { en: 'Default Questionnaire', ar: 'الإستبيان الافتراضي' },
      score,
      questionerAnswers,
      maxScore,
      periodId
    );
  } catch (error) {
    console.error('Error saving legacy user score:', error);
    throw error;
  }
}

// Legacy function for backward compatibility
export async function getUserScore(userId: string, periodId?: string): Promise<UserScore | null> {
  try {
    const response = await getUserLatestScore(userId, periodId);
    return response ? convertToLegacyUserScore(response) : null;
  } catch (error) {
    console.error('Error getting legacy user score:', error);
    return null;
  }
}

// Legacy function for backward compatibility
export async function getUserScoreHistory(userId: string): Promise<UserScore[]> {
  try {
    const responses = await getUserQuestionerHistory(userId);
    return responses.map(convertToLegacyUserScore);
  } catch (error) {
    console.error('Error getting legacy user score history:', error);
    return [];
  }
}

// Legacy function for backward compatibility
export async function hasCompletedQuestionnaire(userId: string, periodId?: string): Promise<boolean> {
  try {
    const response = await getUserLatestScore(userId, periodId);
    return response !== null;
  } catch (error) {
    console.error('Error checking legacy questionnaire completion:', error);
    return false;
  }
}

// Legacy function for backward compatibility
export async function canCreateNewQuestionnaire(userId: string): Promise<boolean> {
  try {
    const currentPeriod = getCurrentPeriod();
    return !(await hasCompletedQuestionnaire(userId, currentPeriod.id));
  } catch (error) {
    console.error('Error checking if can create new legacy questionnaire:', error);
    return false;
  }
}
